import nl from '../i18n/locales/nl.json';
import en from '../i18n/locales/en.json';
import fr from '../i18n/locales/fr.json';
import de from '../i18n/locales/de.json';

export type SupportedLocale = 'nl' | 'fr' | 'en' | 'de';

type Params = Record<string, string | number>;
type TranslationValue = string | ((params?: Params) => string);
type Dictionary = Record<string, TranslationValue>;

const STORAGE_KEY = 'rentcontrol.locale';
const DEFAULT_LOCALE: SupportedLocale = 'en';
const SUPPORTED_LOCALES: SupportedLocale[] = ['nl', 'fr', 'en', 'de'];
const LOCALE_EVENT = 'rc:localechange';

const DATE_LOCALES: Record<SupportedLocale, string> = {
  nl: 'nl-BE',
  fr: 'fr-BE',
  en: 'en-GB',
  de: 'de-DE',
};

const baseDictionaries: Record<SupportedLocale, Record<string, string>> = {
  en,
  nl: { ...en, ...nl },
  fr: { ...en, ...fr },
  de: { ...en, ...de },
};

const runtimeDictionary: Dictionary = {
  'common.daysShort': (params) => `${params?.count ?? 0}d`,
  'common.daysAgo': (params) => {
    const count = Number(params?.count ?? 0);
    return `${count} ${i18nService.t(count === 1 ? 'time.dayAgo' : 'time.daysAgo')}`;
  },
  'common.daysRemaining': (params) => {
    const count = Number(params?.count ?? 0);
    return `${count} ${i18nService.t(count === 1 ? 'time.dayRemaining' : 'time.daysRemaining')}`;
  },
};

baseDictionaries.nl['time.dayAgo'] = 'dag geleden';
baseDictionaries.nl['time.daysAgo'] = 'dagen geleden';
baseDictionaries.nl['time.dayRemaining'] = 'dag resterend';
baseDictionaries.nl['time.daysRemaining'] = 'dagen resterend';

baseDictionaries.en['time.dayAgo'] = 'day ago';
baseDictionaries.en['time.daysAgo'] = 'days ago';
baseDictionaries.en['time.dayRemaining'] = 'day remaining';
baseDictionaries.en['time.daysRemaining'] = 'days remaining';

baseDictionaries.fr['time.dayAgo'] = 'jour passe';
baseDictionaries.fr['time.daysAgo'] = 'jours passes';
baseDictionaries.fr['time.dayRemaining'] = 'jour restant';
baseDictionaries.fr['time.daysRemaining'] = 'jours restants';

baseDictionaries.de['time.dayAgo'] = 'Tag her';
baseDictionaries.de['time.daysAgo'] = 'Tage her';
baseDictionaries.de['time.dayRemaining'] = 'Tag verbleibend';
baseDictionaries.de['time.daysRemaining'] = 'Tage verbleibend';

const dictionaries: Record<SupportedLocale, Dictionary> = {
  nl: { ...baseDictionaries.nl, ...runtimeDictionary },
  fr: { ...baseDictionaries.fr, ...runtimeDictionary },
  en: { ...baseDictionaries.en, ...runtimeDictionary },
  de: { ...baseDictionaries.de, ...runtimeDictionary },
};

function interpolate(template: string, params?: Params): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (_, key) => String(params[key] ?? ''));
}

function detectBrowserLocale(): SupportedLocale {
  const stored = globalThis.localStorage?.getItem(STORAGE_KEY);
  if (stored && SUPPORTED_LOCALES.includes(stored as SupportedLocale)) {
    return stored as SupportedLocale;
  }

  const browserLocale = globalThis.navigator?.language?.toLowerCase() ?? '';
  const match = SUPPORTED_LOCALES.find((locale) => browserLocale.startsWith(locale));
  return match ?? DEFAULT_LOCALE;
}

class I18nService {
  private locale: SupportedLocale = detectBrowserLocale();

  get currentLocale(): SupportedLocale {
    return this.locale;
  }

  get supportedLocales(): SupportedLocale[] {
    return [...SUPPORTED_LOCALES];
  }

  get dateLocale(): string {
    return DATE_LOCALES[this.locale];
  }

  t(key: string, params?: Params): string {
    const dict = dictionaries[this.locale];
    const fallback = dictionaries.en;
    const value = dict[key] ?? fallback[key] ?? key;
    if (typeof value === 'function') return value(params);
    return interpolate(value, params);
  }

  setLocale(locale: SupportedLocale): void {
    if (locale === this.locale) return;
    this.locale = locale;
    globalThis.localStorage?.setItem(STORAGE_KEY, locale);
    this.applyDocumentTranslations();
    globalThis.window?.dispatchEvent?.(new CustomEvent(LOCALE_EVENT, { detail: { locale } }));
  }

  init(): void {
    this.applyDocumentTranslations();
  }

  formatDate(date: string | Date, options?: Intl.DateTimeFormatOptions): string {
    const value = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(value.getTime())) return typeof date === 'string' ? date : '';
    return value.toLocaleDateString(this.dateLocale, options ?? { day: 'numeric', month: 'short', year: 'numeric' });
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat(this.dateLocale, {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }

  applyDocumentTranslations(root: ParentNode = globalThis.document): void {
    if (!root) return;

    root.querySelectorAll<HTMLElement>('[data-i18n]').forEach((element) => {
      const key = element.dataset.i18n;
      if (key) element.innerHTML = this.t(key);
    });

    root.querySelectorAll<HTMLElement>('[data-i18n-placeholder]').forEach((element) => {
      const key = element.dataset.i18nPlaceholder;
      if (key) (element as HTMLInputElement).placeholder = this.t(key);
    });

    root.querySelectorAll<HTMLElement>('[data-i18n-title]').forEach((element) => {
      const key = element.dataset.i18nTitle;
      if (key) element.title = this.t(key);
    });

    root.querySelectorAll<HTMLElement>('[data-i18n-aria-label]').forEach((element) => {
      const key = element.dataset.i18nAriaLabel;
      if (key) element.setAttribute('aria-label', this.t(key));
    });

    if (globalThis.document && root === globalThis.document) {
      document.documentElement.lang = this.locale;
      const titleKey = document.body.dataset.pageTitleKey;
      const siteTitle = this.t('site.title');
      document.title = titleKey ? `${this.t(titleKey)} - ${siteTitle}` : siteTitle;

      const descriptionKey = document.body.dataset.pageDescriptionKey;
      const description = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
      if (description) description.content = this.t(descriptionKey || 'site.description');
    }
  }
}

export const i18nService = new I18nService();

export function t(key: string, params?: Params): string {
  return i18nService.t(key, params);
}

export function onLocaleChange(handler: () => void): () => void {
  const wrapped = () => handler();
  globalThis.window?.addEventListener?.(LOCALE_EVENT, wrapped);
  return () => globalThis.window?.removeEventListener?.(LOCALE_EVENT, wrapped);
}

export function translatePropertyType(type: string): string {
  const labels: Record<string, string> = {
    Appartement: t('property.type.apartment'),
    Huis: t('property.type.house'),
    Studio: t('property.type.studio'),
    Commercieel: t('property.type.commercial'),
    Garage: t('property.type.garage'),
    Ander: t('property.type.other'),
  };
  return labels[type] ?? type;
}

export function translatePropertyStatus(status: string): string {
  const labels: Record<string, string> = {
    Rented: t('property.status.rented'),
    Vacant: t('property.status.vacant'),
    UnderRenovation: t('property.status.underRenovation'),
    ForSale: t('property.status.forSale'),
  };
  return labels[status] ?? status;
}

export function translateDocumentType(type: string): string {
  const labels: Record<string, string> = {
    Epc: t('document.type.epc'),
    Inspection: t('document.type.inspection'),
    Insurance: t('document.type.insurance'),
    Invoice: t('document.type.invoice'),
    Photo: t('document.type.photo'),
    Other: t('document.type.other'),
  };
  return labels[type] ?? type;
}

export function translateMaintenanceActivityCategory(category: string): string {
  const labels: Record<string, string> = {
    Maintenance: t('maintenanceActivities.category.maintenance'),
    Repair: t('maintenanceActivities.category.repair'),
    Inspection: t('maintenanceActivities.category.inspection'),
    Renovation: t('maintenanceActivities.category.renovation'),
    Other: t('maintenanceActivities.category.other'),
  };
  return labels[category] ?? category;
}

export function translateEventType(type: string): string {
  const labels: Record<string, string> = {
    MoveIn: t('event.moveIn'),
    MoveOut: t('event.moveOut'),
    Damage: t('event.damage'),
    Inspection: t('event.inspection'),
    Indexation: t('event.indexation'),
    Remark: t('event.remark'),
    PropertyStatusChange: t('event.propertyStatusChange'),
  };
  return labels[type] ?? type;
}

export function translateTimelineTitle(title: string, titleKey?: string | null): string {
  return titleKey ? t(titleKey) : title;
}

export function translatePlaceDescriptionKind(kind: string): string {
  const labels: Record<string, string> = {
    MoveIn: t('placeDescription.kind.moveIn'),
    MoveOut: t('placeDescription.kind.moveOut'),
  };
  return labels[kind] ?? kind;
}

export function translatePlaceDescriptionCondition(condition: string): string {
  const labels: Record<string, string> = {
    Good: t('placeDescription.condition.good'),
    NeedsAttention: t('placeDescription.condition.needsAttention'),
    Damaged: t('placeDescription.condition.damaged'),
    NotApplicable: t('placeDescription.condition.notApplicable'),
  };
  return labels[condition] ?? condition;
}
